import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiCall, apiCallText } from '../utils/apiUtils';
import { API_BASE_URL } from '../constants/migrationConstants';

// Connection thunks
export const fetchSailpointConnection = createAsyncThunk(
  'migration/fetchSailpointConnection',
  async () => apiCall(`${API_BASE_URL}/getSailpointConnection`)
);

export const fetchOIMConnection = createAsyncThunk('migration/fetchOIMConnection', async () =>
  apiCall(`${API_BASE_URL}/getOIMConnection`)
);

export const fetchAllSailpointConnections = createAsyncThunk(
  'migration/fetchAllSailpointConnections',
  async () => {
    const response = await apiCall(
      `${API_BASE_URL}/connections/get-all-connections?type=SailpointIIQ`
    );
    return response;
  }
);

export const fetchAllOIMConnections = createAsyncThunk(
  'migration/fetchAllOIMConnections',
  async () => {
    const response = await apiCall(`${API_BASE_URL}/connections/get-all-connections?type=OIM`);
    return response;
  }
);

export const saveConnection = createAsyncThunk(
  'migration/saveConnection',
  async ({ configKey, connectionName, category }, { rejectWithValue }) => {
    try {
      return await apiCall(`${API_BASE_URL}/connections/save-connection`, {
        method: 'POST',
        body: JSON.stringify({ configKey, connectionName, category }),
      });
    } catch (error) {
      return rejectWithValue({ data: error.data, message: error.message });
    }
  }
);

// Discovery thunks
export const fetchSourceStatus = createAsyncThunk('migration/fetchSourceStatus', async (sourceId) =>
  apiCall(`${API_BASE_URL}/sources/${sourceId}/status`)
);

export const fetchDiscoveryStatus = createAsyncThunk(
  'migration/fetchDiscoveryStatus',
  async (sourceId) => apiCall(`${API_BASE_URL}/sources/${sourceId}/status`)
);

export const fetchSourceRawData = createAsyncThunk(
  'migration/fetchSourceRawData',
  async ({ sourceId, objectId }) => {
    const url = `${API_BASE_URL}/sources/${sourceId}/objects/${objectId}/source-raw-data`;
    try {
      // Try JSON first (OIM returns HTML-encoded XML wrapped in JSON)
      const res = await apiCall(url);
      return typeof res?.data === 'string' ? res.data : res;
    } catch {
      // Fallback to raw text (SailPoint returns plain XML)
      return apiCallText(url);
    }
  }
);

// Object thunks
export const fetchObjects = createAsyncThunk(
  'migration/fetchObjects',
  async ({ sourceId, objectType }) =>
    apiCall(`${API_BASE_URL}/sources/${sourceId}/objects?filter.type=${objectType}`)
);

export const fetchDataMigrationObjects = createAsyncThunk(
  'migration/fetchDataMigrationObjects',
  async ({ sourceId, objectType }) =>
    apiCall(
      `${API_BASE_URL}/sources/${sourceId}/objects?filter.type=${objectType}&filter.custom=DATA_MIG`
    )
);

export const fetchVisibilityStats = createAsyncThunk(
  'migration/fetchVisibilityStats',
  async ({ sourceId }) => apiCall(`${API_BASE_URL}/sources/${sourceId}/visibility-stats`)
);

// Configuration thunks
export const generateSavConfig = createAsyncThunk(
  'migration/generateSavConfig',
  async ({ sourceId, objectIds, objectType }) =>
    apiCall(`${API_BASE_URL}/sources/${sourceId}/objects/sav-config`, {
      method: 'POST',
      body: JSON.stringify({ objectIds, objectType }),
    })
);

export const bulkOnboardObjects = createAsyncThunk(
  'migration/bulkOnboardObjects',
  async ({ sourceId, objectType, objects, objectIds }, { rejectWithValue }) => {
    try {
      return await apiCall(`${API_BASE_URL}/sources/${sourceId}/objects/bulk-onboard`, {
        method: 'POST',
        body: JSON.stringify(objectIds ? { objectIds, objectType } : { objectType, objects }),
      });
    } catch (error) {
      return rejectWithValue({ data: error.data, message: error.message });
    }
  }
);

export const bulkOnboardObjectsV2 = createAsyncThunk(
  'migration/bulkOnboardObjectsV2',
  async ({ sourceId, objectType, objectIds }, { rejectWithValue }) => {
    try {
      return await apiCall(`${API_BASE_URL}/sources/${sourceId}/objects/v2/bulk-onboard`, {
        method: 'POST',
        body: JSON.stringify({ objectType, objectIds }),
      });
    } catch (error) {
      return rejectWithValue({ data: error.data, message: error.message });
    }
  }
);

// migration thunks
export const bulkMigrateDataset = createAsyncThunk(
  'migration/bulkMigrateDataset',
  async ({ sourceId, objectIds, objectType }) =>
    apiCall(`${API_BASE_URL}/sources/${sourceId}/datasets/bulk-migrate-dataset`, {
      method: 'POST',
      body: JSON.stringify({ objectIds, objectType }),
    })
);

export const bulkDataMigrate = createAsyncThunk(
  'migration/bulkDataMigrate',
  async ({ sourceId, objectType, objectIds, executeJobTriggers = false }, { rejectWithValue }) => {
    try {
      return await apiCall(`${API_BASE_URL}/sources/${sourceId}/objects/bulk-data-migrate`, {
        method: 'POST',
        body: JSON.stringify({ objectType, objectIds, executeJobTriggers }),
      });
    } catch (error) {
      return rejectWithValue({ data: error.data, message: error.message });
    }
  }
);

export const migrateIdentities = createAsyncThunk('migration/migrateIdentities', async (sourceId) =>
  apiCall(`${API_BASE_URL}/sources/${sourceId}/users/data-migration?executeJobTriggers=true`, {
    method: 'POST',
  })
);

export const fetchUserAttributes = createAsyncThunk(
  'migration/fetchUserAttributes',
  async (sourceId) => {
    const response = await apiCall(`${API_BASE_URL}/sources/${sourceId}/user-attributes`);
    return response.data;
  }
);

export const fetchMigrationDisabledObjects = createAsyncThunk(
  'migration/fetchMigrationDisabledObjects',
  async ({ sourceId, objectType }) =>
    apiCall(
      `${API_BASE_URL}/sources/${sourceId}/objects/${objectType.toLowerCase()}/migration-disabled`
    )
);

export const saveConfig = createAsyncThunk(
  'migration/saveConfig',
  async ({ configValue }, { rejectWithValue }) => {
    try {
      return await apiCall(`${API_BASE_URL}/config`, {
        method: 'POST',
        body: JSON.stringify({
          configName: 'DATA_MIGRATION_ENABLED',
          configValue,
          category: 'MIGRATION',
        }),
      });
    } catch (error) {
      return rejectWithValue({ data: error.data, message: error.message });
    }
  }
);

export const saveAiAgentConfig = createAsyncThunk(
  'migration/saveAiAgentConfig',
  async ({ configValue }, { rejectWithValue }) => {
    try {
      return await apiCall(`${API_BASE_URL}/config`, {
        method: 'POST',
        body: JSON.stringify({
          configName: 'USEAISERVICE',
          configValue,
          category: 'AI_AGENT',
        }),
      });
    } catch (error) {
      return rejectWithValue({ data: error.data, message: error.message });
    }
  }
);

export const saveDisconnectedDiscoveryConfig = createAsyncThunk(
  'migration/saveDisconnectedDiscoveryConfig',
  async ({ configValue }, { rejectWithValue }) => {
    try {
      return await apiCall(`${API_BASE_URL}/config`, {
        method: 'POST',
        body: JSON.stringify({
          configName: 'DISCONNECTED_DISCOVERY',
          configValue,
          category: 'MIGRATION',
        }),
      });
    } catch (error) {
      return rejectWithValue({ data: error.data, message: error.message });
    }
  }
);

export const triggerConnectedDiscovery = createAsyncThunk(
  'migration/triggerConnectedDiscovery',
  async ({ sourceId }, { rejectWithValue }) => {
    try {
      return await apiCall(`${API_BASE_URL}/sources/${sourceId}/connected-discovery`, {
        method: 'POST',
      });
    } catch (error) {
      return rejectWithValue({ data: error.data, message: error.message });
    }
  }
);

export const uploadDisconnectedDiscoveryFile = createAsyncThunk(
  'migration/uploadDisconnectedDiscoveryFile',
  async ({ sourceId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      return await apiCall(`${API_BASE_URL}/sources/${sourceId}/disconnected-discovery`, {
        method: 'POST',
        headers: {},
        body: formData,
      });
    } catch (error) {
      return rejectWithValue({
        data: error.data,
        message: error.message || 'Failed to upload file',
      });
    }
  }
);

export const saveUserAttributeMapping = createAsyncThunk(
  'migration/saveUserAttributeMapping',
  async ({ sourceId, userAttributeMapping }, { rejectWithValue }) => {
    try {
      return await apiCall(`${API_BASE_URL}/sources/${sourceId}/user-attribute-mapping`, {
        method: 'PUT',
        body: JSON.stringify({ userAttributeMapping }),
      });
    } catch (error) {
      return rejectWithValue({ data: error.data, message: error.message });
    }
  }
);
